import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatGermanTime } from '../../helpers/germanTime';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import { fetchData, postData } from '../../helpers/apiUtils';

// Keep aligned with backend ChangeKind / SourceRef in CopyRefreshService.ts.
type ChangeKind =
  | 'date_absolute'
  | 'quarter'
  | 'forecast_horizon'
  | 'relative_day'
  | 'spam_caps'
  | 'spam_extreme_percent'
  | 'spam_repetition'
  | 'other';

type ChangeField = 'subject' | 'preview_text' | 'html_body';

interface SourceRef {
  url: string;
  title: string;
  snippet?: string;
  published_at?: string;
}

interface ProposalChange {
  change_index: number;
  change_kind: ChangeKind;
  field: ChangeField;
  original_text: string;
  proposed_text: string;
  reasoning: string;
  source_urls: SourceRef[];
  confidence: number;
}

interface ProposalMeta {
  campaignId: number;
  sourceCampaignId: number | null;
  emailId: number;
  scheduledDatetime: string;
  contentHash: string;
  llmModel: string;
  llmTokensInput: number;
  llmTokensOutput: number;
  llmCostEur: number;
  llmSearchCount: number;
}

interface PersistedProposal {
  proposalId: string;
  changes: ProposalChange[];
  meta: ProposalMeta;
  createdAt: string;
  status: 'proposed' | 'accepted' | 'rejected' | 'superseded' | 'mixed';
}

interface ApplyResponse {
  updatedHtmlBody: string;
  updatedSubject: string;
  updatedPreviewText: string;
  appliedCount: number;
  rejectedCount: number;
}

interface Props {
  open: boolean;
  campaignId: number;
  emailId: number | undefined;
  scheduledDatetime: string;
  onClose: () => void;
  onApplied: (result: ApplyResponse) => void;
}

type Decision = 'accept' | 'reject' | null;

type PanelState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'proposed'; proposal: PersistedProposal; decisions: Record<number, Decision> }
  | { kind: 'applying'; proposal: PersistedProposal; decisions: Record<number, Decision> }
  | { kind: 'applied'; appliedCount: number; rejectedCount: number }
  | { kind: 'error'; message: string };

const DATE_KINDS: ChangeKind[] = ['date_absolute', 'quarter', 'forecast_horizon', 'relative_day'];
const SPAM_KINDS: ChangeKind[] = ['spam_caps', 'spam_extreme_percent', 'spam_repetition'];

function describeKind(kind: ChangeKind): string {
  switch (kind) {
    case 'date_absolute':
      return 'Datums-Anker';
    case 'quarter':
      return 'Quartals-Bezug';
    case 'forecast_horizon':
      return 'Prognose-Horizont';
    case 'relative_day':
      return 'Tages-Bezug';
    case 'spam_caps':
      return 'ALL-CAPS softening';
    case 'spam_extreme_percent':
      return 'Extreme % softening';
    case 'spam_repetition':
      return 'Wiederholung softening';
    case 'other':
      return 'Sonstige';
  }
}

function describeField(field: ChangeField): string {
  switch (field) {
    case 'subject':
      return 'Betreff';
    case 'preview_text':
      return 'Preheader';
    case 'html_body':
      return 'Body';
  }
}

function confidenceColor(c: number): 'success' | 'warning' | 'error' {
  if (c >= 0.7) return 'success';
  if (c >= 0.4) return 'warning';
  return 'error';
}

function truncate(s: string, max: number): string {
  if (!s) return '';
  if (s.length <= max) return s;
  return s.substring(0, max - 1).trimEnd() + '…';
}

function decisionSummary(decisions: Record<number, Decision>) {
  let accepted = 0;
  let rejected = 0;
  let open = 0;
  for (const v of Object.values(decisions)) {
    if (v === 'accept') accepted += 1;
    else if (v === 'reject') rejected += 1;
    else open += 1;
  }
  return { accepted, rejected, open };
}

const CopyRefreshPanel: React.FC<Props> = ({
  open,
  campaignId,
  emailId,
  scheduledDatetime,
  onClose,
  onApplied,
}) => {
  const [state, setState] = useState<PanelState>({ kind: 'idle' });

  // Reset state when the panel re-opens or the email changes — a fresh email
  // is a fresh decision surface.
  useEffect(() => {
    if (!open) return;
    setState({ kind: 'idle' });
  }, [open, emailId, campaignId]);

  // Try to load a cached proposal on first open. If one exists (created within
  // cooldown for the same content_hash), resume with it instead of starting
  // from idle.
  useEffect(() => {
    if (!open || !emailId || state.kind !== 'idle') return;
    let cancelled = false;
    fetchData<PersistedProposal>(
      `/api/plx-mailings/campaigns/${campaignId}/emails/${emailId}/refresh-proposal/latest`,
    )
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data && res.data.status === 'proposed') {
          const decisions: Record<number, Decision> = {};
          for (const c of res.data.changes) decisions[c.change_index] = null;
          setState({ kind: 'proposed', proposal: res.data, decisions });
        }
      })
      .catch(() => {
        // Silent — idle is the default fallback.
      });
    return () => {
      cancelled = true;
    };
  }, [open, emailId, campaignId, state.kind]);

  const runProposal = useCallback(
    async (opts: { force?: boolean } = {}) => {
      if (!emailId) return;
      setState({ kind: 'loading' });
      try {
        const res = await postData<PersistedProposal>(
          `/api/plx-mailings/campaigns/${campaignId}/emails/${emailId}/refresh-proposal`,
          opts.force ? { force: true } : {},
        );
        if (res.success && res.data) {
          const proposal = res.data;
          const decisions: Record<number, Decision> = {};
          for (const c of proposal.changes) decisions[c.change_index] = null;
          setState({ kind: 'proposed', proposal, decisions });
        } else {
          setState({
            kind: 'error',
            message: (res as any).error || 'Refresh-Vorschlag konnte nicht erstellt werden.',
          });
        }
      } catch (err: any) {
        setState({
          kind: 'error',
          message: err?.message || 'Refresh-Vorschlag konnte nicht erstellt werden.',
        });
      }
    },
    [campaignId, emailId],
  );

  const setDecision = useCallback(
    (idx: number, decision: Decision) => {
      setState((prev) => {
        if (prev.kind !== 'proposed') return prev;
        const nextDecisions = { ...prev.decisions, [idx]: decision };
        return { ...prev, decisions: nextDecisions };
      });
    },
    [],
  );

  const acceptAllOfKind = useCallback(
    (kinds: ChangeKind[]) => {
      setState((prev) => {
        if (prev.kind !== 'proposed') return prev;
        const next = { ...prev.decisions };
        for (const c of prev.proposal.changes) {
          if (kinds.includes(c.change_kind) && next[c.change_index] === null) {
            next[c.change_index] = 'accept';
          }
        }
        return { ...prev, decisions: next };
      });
    },
    [],
  );

  const applyDecisions = useCallback(async () => {
    if (state.kind !== 'proposed' || !emailId) return;
    const accepted: number[] = [];
    const rejected: number[] = [];
    for (const [k, v] of Object.entries(state.decisions)) {
      if (v === 'accept') accepted.push(Number(k));
      else if (v === 'reject') rejected.push(Number(k));
    }

    setState({ ...state, kind: 'applying' });
    try {
      const res = await postData<ApplyResponse>(
        `/api/plx-mailings/campaigns/${campaignId}/emails/${emailId}/refresh-proposal/${state.proposal.proposalId}/apply`,
        { acceptedChangeIndexes: accepted, rejectedChangeIndexes: rejected },
      );
      if (res.success && res.data) {
        setState({
          kind: 'applied',
          appliedCount: res.data.appliedCount,
          rejectedCount: res.data.rejectedCount,
        });
        onApplied(res.data);
      } else {
        setState({
          kind: 'error',
          message: (res as any).error || 'Apply fehlgeschlagen.',
        });
      }
    } catch (err: any) {
      setState({
        kind: 'error',
        message: err?.message || 'Apply fehlgeschlagen.',
      });
    }
  }, [state, campaignId, emailId, onApplied]);

  const counts = useMemo(() => {
    if (state.kind === 'proposed' || state.kind === 'applying') {
      return decisionSummary(state.decisions);
    }
    return { accepted: 0, rejected: 0, open: 0 };
  }, [state]);

  const proposal = state.kind === 'proposed' || state.kind === 'applying' ? state.proposal : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 540 }, p: 0 } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <AutoFixHighIcon color="primary" />
          <Typography variant="h6" sx={{ flex: 1 }}>
            Copy-Refresh
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {state.kind === 'idle' && (
            <Stack spacing={2}>
              <Alert severity="info">
                Der KI-gestützte Refresh prüft Datums-Anker, Q-Prognosen und Spam-Signale für den
                Sendetermin am{' '}
                {/* scheduled_datetime ist Berlin-Wallclock — plain new Date() zeigte +2h */}
                <strong>{formatGermanTime(scheduledDatetime, 'DD.MM.YYYY, HH:mm')}</strong>. Jeder
                Vorschlag muss durch eine Web-Quelle belegt sein.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Modell: <strong>Claude Opus 4.7</strong> · Max. 10 Web-Recherchen pro Lauf
              </Typography>
              <Button
                variant="contained"
                startIcon={<AutoFixHighIcon />}
                onClick={() => runProposal()}
                disabled={!emailId}
                size="large"
              >
                Refresh-Vorschlag erstellen
              </Button>
            </Stack>
          )}

          {state.kind === 'loading' && (
            <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Recherche &amp; Analyse läuft (bis zu 60 s)…
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Claude Opus 4.7 prüft veraltete Anker und schlägt minimale Refreshes vor.
              </Typography>
            </Stack>
          )}

          {state.kind === 'error' && (
            <Stack spacing={2}>
              <Alert severity="error">{state.message}</Alert>
              <Button
                variant="outlined"
                startIcon={<RefreshOutlinedIcon />}
                onClick={() => runProposal({ force: true })}
              >
                Erneut versuchen
              </Button>
            </Stack>
          )}

          {state.kind === 'applied' && (
            <Stack spacing={2}>
              <Alert severity="success">
                <strong>Copy-Refresh angewendet</strong> — {state.appliedCount} von{' '}
                {state.appliedCount + state.rejectedCount} Änderungen übernommen.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Der Editor zeigt die aktualisierte Version. Originaler Quelltext bleibt im Audit-Log
                ({state.appliedCount} accepted / {state.rejectedCount} rejected rows).
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshOutlinedIcon />}
                onClick={() => runProposal({ force: true })}
              >
                Erneut prüfen
              </Button>
            </Stack>
          )}

          {proposal && (state.kind === 'proposed' || state.kind === 'applying') && (
            <Stack spacing={2}>
              {/* Stats header */}
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ rowGap: 1 }}>
                  <Tooltip title="LLM-Modell">
                    <Chip
                      size="small"
                      label={proposal.meta.llmModel}
                      variant="outlined"
                      color="primary"
                    />
                  </Tooltip>
                  <Tooltip title="Web-Recherchen (gerundet aus Quellen-Count)">
                    <Chip size="small" label={`🔍 ${proposal.meta.llmSearchCount}/10`} />
                  </Tooltip>
                  <Tooltip title="Geschätzte Kosten">
                    <Chip
                      size="small"
                      label={`€ ${proposal.meta.llmCostEur.toFixed(4)}`}
                    />
                  </Tooltip>
                  <Box sx={{ flex: 1 }} />
                  <Chip
                    size="small"
                    color="success"
                    icon={<CheckCircleOutlineIcon />}
                    label={`Akzeptiert: ${counts.accepted}`}
                  />
                  <Chip
                    size="small"
                    color="error"
                    icon={<HighlightOffIcon />}
                    label={`Abgelehnt: ${counts.rejected}`}
                  />
                  <Chip
                    size="small"
                    color={counts.open > 0 ? 'warning' : 'default'}
                    label={`Offen: ${counts.open}`}
                  />
                </Stack>
              </Paper>

              {proposal.changes.length === 0 ? (
                <Alert severity="info">
                  Der LLM-Lauf hat keine Refresh-Vorschläge erzeugt — die Kampagne wirkt für den
                  Sendetermin frisch. Audit-Log enthält trotzdem einen Eintrag.
                </Alert>
              ) : (
                <>
                  {/* Bulk-accept shortcuts */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Schnellaktionen
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => acceptAllOfKind(DATE_KINDS)}
                        disabled={state.kind === 'applying'}
                      >
                        Alle Datums-Refreshes akzeptieren
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => acceptAllOfKind(SPAM_KINDS)}
                        disabled={state.kind === 'applying'}
                      >
                        Alle Spam-Softenings akzeptieren
                      </Button>
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Per-change accordions */}
                  {proposal.changes.map((change) => {
                    const decision = state.decisions[change.change_index];
                    return (
                      <Accordion key={change.change_index} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                            <Chip
                              size="small"
                              label={describeKind(change.change_kind)}
                              color={
                                DATE_KINDS.includes(change.change_kind)
                                  ? 'primary'
                                  : SPAM_KINDS.includes(change.change_kind)
                                    ? 'warning'
                                    : 'default'
                              }
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={describeField(change.field)}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {truncate(change.original_text, 40)} →{' '}
                                <Box component="span" sx={{ fontWeight: 600 }}>
                                  {truncate(change.proposed_text, 40)}
                                </Box>
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              color={confidenceColor(change.confidence)}
                              label={change.confidence.toFixed(2)}
                            />
                            {decision === 'accept' && (
                              <CheckCircleOutlineIcon color="success" fontSize="small" />
                            )}
                            {decision === 'reject' && (
                              <HighlightOffIcon color="error" fontSize="small" />
                            )}
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={1.5}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Original
                              </Typography>
                              <Box
                                sx={{
                                  p: 1,
                                  backgroundColor: '#fef2f2',
                                  borderRadius: 1,
                                  fontFamily: 'monospace',
                                  fontSize: '0.85rem',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {change.original_text}
                              </Box>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Vorschlag
                              </Typography>
                              <Box
                                sx={{
                                  p: 1,
                                  backgroundColor: '#ecfdf5',
                                  borderRadius: 1,
                                  fontFamily: 'monospace',
                                  fontSize: '0.85rem',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {change.proposed_text}
                              </Box>
                            </Box>
                            {change.reasoning && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Begründung
                                </Typography>
                                <Typography variant="body2">{change.reasoning}</Typography>
                              </Box>
                            )}
                            {change.source_urls.length > 0 && (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Web-Quellen ({change.source_urls.length})
                                </Typography>
                                <Stack spacing={0.5}>
                                  {change.source_urls.map((src, i) => (
                                    <Link
                                      key={i}
                                      href={src.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        fontSize: '0.85rem',
                                      }}
                                    >
                                      <LaunchIcon sx={{ fontSize: 14 }} />
                                      {src.title || src.url}
                                      {src.published_at && (
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          label={src.published_at}
                                          sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                                        />
                                      )}
                                    </Link>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                            {change.source_urls.length === 0 && (
                              <Alert severity="warning" sx={{ py: 0.5 }}>
                                Keine Web-Quellen für diese Änderung — Konfidenz reduziert. Manuell
                                gründlich prüfen.
                              </Alert>
                            )}

                            {/* Decision radio */}
                            <FormControl>
                              <RadioGroup
                                row
                                value={decision || ''}
                                onChange={(e) =>
                                  setDecision(
                                    change.change_index,
                                    e.target.value as Decision,
                                  )
                                }
                              >
                                <FormControlLabel
                                  value="accept"
                                  control={<Radio size="small" color="success" />}
                                  label="Akzeptieren"
                                  disabled={state.kind === 'applying'}
                                />
                                <FormControlLabel
                                  value="reject"
                                  control={<Radio size="small" color="error" />}
                                  label="Ablehnen"
                                  disabled={state.kind === 'applying'}
                                />
                              </RadioGroup>
                            </FormControl>
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </>
              )}
            </Stack>
          )}
        </Box>

        {/* Footer */}
        {(state.kind === 'proposed' || state.kind === 'applying') && proposal && proposal.changes.length > 0 && (
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
            <Stack direction="row" spacing={1}>
              <Button onClick={onClose} disabled={state.kind === 'applying'}>
                Verwerfen
              </Button>
              <Box sx={{ flex: 1 }} />
              <Tooltip
                title={
                  counts.open > 0
                    ? `Bitte zuerst alle ${counts.open} offenen Vorschläge entscheiden`
                    : ''
                }
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={
                      state.kind === 'applying' ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <CheckCircleOutlineIcon />
                      )
                    }
                    disabled={counts.open > 0 || state.kind === 'applying'}
                    onClick={applyDecisions}
                  >
                    Anwenden ({counts.accepted})
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default CopyRefreshPanel;
